import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ReportType, ShapeType, FilterType } from '../../../reportsTypes';
import { isEqual } from 'lodash';
import { ReportFilterType, ShapeDictionaryType } from '../../../utils';
import { ReportCloneValues } from '../../utils/createReportCloneFormSchema';
import { buildReportFormModel } from '../../utils/reportFormModel';
import { Ship } from '../../utils/createDynamicFilterZodSchema';
import type {
  FilterCollection,
  PortCollection,
  ShapeCollection,
} from '../../utils/formValueBuilders';

interface Params {
  form: UseFormReturn<ReportCloneValues>;
  reportType: ReportType;
  shapes: ShapeCollection | undefined;
  filters: FilterCollection | undefined;
  ports?: PortCollection;
  shapeType: ShapeType;
  filterType: FilterType;
  reportTemplateNameValue: string;
  firstReportNameValue: string;
  dateRangeValue?: { from: Date; to: Date };
  miscellaneousValue?: Record<string, unknown>;
  shapeDictionaryValue: ShapeDictionaryType;
  filterValue: ReportFilterType | null;
  selectedGroupsValue: unknown[];
  instantShape: boolean;
  setReportTemplateName: (v: string | undefined) => void;
  setFirstReportName: (v: string | undefined) => void;
  setDateRange: (v: { from: Date; to: Date }) => void;
  setEmail: (v: string[] | undefined) => void;
  setPhone: (v: string | undefined) => void;
  setCountryCode: (v: string | undefined) => void;
  setSelectedGroups: Dispatch<SetStateAction<unknown[]>>;
  setMiscellaneous: Dispatch<SetStateAction<Record<string, unknown>>>;
  setShapeDictionary: Dispatch<SetStateAction<ShapeDictionaryType>>;
  setFilter: Dispatch<SetStateAction<ReportFilterType | null>>;
  setShips: Dispatch<SetStateAction<Ship[]>>;
  setShipAttribute?: Dispatch<
    SetStateAction<'colorBy' | 'filterBy' | undefined>
  >;
}

export function useReportFormContextBridge({
  form,
  reportType,
  shapes,
  filters,
  ports,
  shapeType,
  filterType,
  reportTemplateNameValue,
  firstReportNameValue,
  dateRangeValue,
  miscellaneousValue,
  shapeDictionaryValue,
  filterValue,
  selectedGroupsValue,
  instantShape,
  setReportTemplateName,
  setFirstReportName,
  setDateRange,
  setEmail,
  setPhone,
  setCountryCode,
  setSelectedGroups,
  setMiscellaneous,
  setShapeDictionary,
  setFilter,
  setShips,
  setShipAttribute,
}: Params) {
  const previousRef = useRef<{
    reportTemplateName: string;
    firstReportName: string;
    dateRange?: { from: number; to: number };
    email: string[];
    phone: string;
    countryCode: string;
    selectedGroups: unknown[];
    miscellaneous: Record<string, unknown>;
    shapeDictionary: ShapeDictionaryType;
    filter: ReportFilterType | null;
  }>({
    reportTemplateName: reportTemplateNameValue ?? '',
    firstReportName: firstReportNameValue ?? '',
    dateRange: dateRangeValue
      ? { from: dateRangeValue.from.getTime(), to: dateRangeValue.to.getTime() }
      : undefined,
    email: Array.isArray(miscellaneousValue?.['email'])
      ? (miscellaneousValue?.['email'] as string[])
      : [],
    phone:
      typeof miscellaneousValue?.['phone'] === 'string'
        ? (miscellaneousValue?.['phone'] as string)
        : '',
    countryCode:
      typeof miscellaneousValue?.['countryCode'] === 'string'
        ? (miscellaneousValue?.['countryCode'] as string)
        : '',
    selectedGroups: Array.isArray(selectedGroupsValue)
      ? selectedGroupsValue
      : [],
    miscellaneous: miscellaneousValue ?? {},
    shapeDictionary: shapeDictionaryValue,
    filter: filterValue,
  });

  useEffect(() => {
    previousRef.current.reportTemplateName = reportTemplateNameValue ?? '';
  }, [reportTemplateNameValue]);

  useEffect(() => {
    previousRef.current.firstReportName = firstReportNameValue ?? '';
  }, [firstReportNameValue]);

  useEffect(() => {
    previousRef.current.dateRange = dateRangeValue
      ? {
          from: dateRangeValue.from.getTime(),
          to: dateRangeValue.to.getTime(),
        }
      : undefined;
  }, [dateRangeValue]);

  useEffect(() => {
    const misc = miscellaneousValue ?? {};
    previousRef.current.miscellaneous = misc;
    previousRef.current.email = Array.isArray(misc['email'])
      ? (misc['email'] as string[])
      : [];
    previousRef.current.phone =
      typeof misc['phone'] === 'string' ? (misc['phone'] as string) : '';
    previousRef.current.countryCode =
      typeof misc['countryCode'] === 'string'
        ? (misc['countryCode'] as string)
        : '';
  }, [miscellaneousValue]);

  useEffect(() => {
    previousRef.current.selectedGroups = Array.isArray(selectedGroupsValue)
      ? selectedGroupsValue
      : [];
  }, [selectedGroupsValue]);

  useEffect(() => {
    previousRef.current.shapeDictionary = shapeDictionaryValue;
  }, [shapeDictionaryValue]);

  useEffect(() => {
    previousRef.current.filter = filterValue;
  }, [filterValue]);

  useEffect(() => {
    const subscription = form.watch((values) => {
      if (!values) return;

      if (values.report_template_name !== undefined) {
        const next =
          typeof values.report_template_name === 'string'
            ? values.report_template_name
            : '';

        if (previousRef.current.reportTemplateName !== next) {
          previousRef.current.reportTemplateName = next;
          setReportTemplateName(
            typeof values.report_template_name === 'string'
              ? values.report_template_name
              : undefined
          );
        }
      }
      if (values.report_name !== undefined) {
        const next =
          typeof values.report_name === 'string' ? values.report_name : '';

        if (previousRef.current.firstReportName !== next) {
          previousRef.current.firstReportName = next;
          setFirstReportName(
            typeof values.report_name === 'string'
              ? values.report_name
              : undefined
          );
        }
      }
      if (values.start_datetime && values.end_datetime) {
        const fromDate =
          values.start_datetime instanceof Date
            ? values.start_datetime
            : typeof values.start_datetime === 'string' ||
              typeof values.start_datetime === 'number'
            ? new Date(values.start_datetime)
            : new Date();
        const toDate =
          values.end_datetime instanceof Date
            ? values.end_datetime
            : typeof values.end_datetime === 'string' ||
              typeof values.end_datetime === 'number'
            ? new Date(values.end_datetime)
            : new Date();

        const nextRange = { from: fromDate.getTime(), to: toDate.getTime() };
        const previousRange = previousRef.current.dateRange;

        if (
          !previousRange ||
          previousRange.from !== nextRange.from ||
          previousRange.to !== nextRange.to
        ) {
          previousRef.current.dateRange = nextRange;
          setDateRange({ from: fromDate, to: toDate });
        }
      }

      const model = buildReportFormModel({
        reportType,
        values: values as Partial<ReportCloneValues>,
        shapeType,
        filterType,
        resources: {
          shapes,
          filters,
          ports,
        },
        existingShapeDictionary: previousRef.current.shapeDictionary ?? null,
        fallbackShapeDictionary: previousRef.current.shapeDictionary ?? null,
        fallbackReportTemplateName: previousRef.current.reportTemplateName,
        fallbackReportName: previousRef.current.firstReportName,
        instantShape,
      });

      const miscFromValues = (values.miscellaneous ?? {}) as Record<
        string,
        unknown
      >;
      const misc = model?.miscellaneous ?? miscFromValues;

      const email = Array.isArray(misc['email'])
        ? (misc['email'] as string[])
        : [];
      if (!isEqual(previousRef.current.email, email)) {
        previousRef.current.email = email;
        setEmail(email);
      }

      const phone =
        typeof misc['phone'] === 'string' ? (misc['phone'] as string) : '';
      if (previousRef.current.phone !== phone) {
        previousRef.current.phone = phone;
        setPhone(phone);
      }

      const countryCode =
        typeof misc['countryCode'] === 'string'
          ? (misc['countryCode'] as string)
          : '';
      if (previousRef.current.countryCode !== countryCode) {
        previousRef.current.countryCode = countryCode;
        setCountryCode(countryCode);
      }

      const selectedGroups = Array.isArray(misc['selectedGroups'])
        ? (misc['selectedGroups'] as unknown[])
        : [];
      if (!isEqual(previousRef.current.selectedGroups, selectedGroups)) {
        previousRef.current.selectedGroups = selectedGroups;
        setSelectedGroups(selectedGroups);
      }

      if (!isEqual(previousRef.current.miscellaneous, misc)) {
        previousRef.current.miscellaneous = misc;
        setMiscellaneous(misc);
      }

      const nextShapeDictionary = model?.shapeDictionary ?? null;
      if (!isEqual(previousRef.current.shapeDictionary, nextShapeDictionary)) {
        previousRef.current.shapeDictionary = nextShapeDictionary;
        setShapeDictionary(nextShapeDictionary);
      }

      const nextFilter = model?.filter ?? null;
      if (!isEqual(previousRef.current.filter, nextFilter)) {
        previousRef.current.filter = nextFilter;
        setFilter(nextFilter);

        // Also sync to ships context when filterType is 'ship'
        if (filterType === 'ship' && Array.isArray(nextFilter)) {
          setShips(nextFilter as Ship[]);
        }
      }

      // Handle ships and shipAttribute (separate from filter)
      if (reportType.ships) {
        const shipsFromValues = (values as Record<string, unknown>)['ships'];
        const shipAttributeFromValues = (values as Record<string, unknown>)[
          'shipAttribute'
        ];

        // Sync ships array
        if (Array.isArray(shipsFromValues)) {
          setShips(shipsFromValues as Ship[]);
        }

        // Sync shipAttribute
        if (
          setShipAttribute &&
          (shipAttributeFromValues === 'colorBy' ||
            shipAttributeFromValues === 'filterBy')
        ) {
          setShipAttribute(shipAttributeFromValues);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    form,
    reportType,
    shapes,
    filters,
    ports,
    shapeType,
    filterType,
    reportTemplateNameValue,
    firstReportNameValue,
    instantShape,
    setReportTemplateName,
    setFirstReportName,
    setDateRange,
    setEmail,
    setPhone,
    setCountryCode,
    setSelectedGroups,
    setMiscellaneous,
    setShapeDictionary,
    setFilter,
    setShips,
    setShipAttribute,
  ]);
}
